import React from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import { Avatar, Box, Drawer, drawerClasses, IconButton, Typography } from '@mui/material';
import { matchPath, useLocation } from 'react-router';

import { Nullable } from '@clearverify/types';

import { useScreenInfo } from 'src/hooks/common';
import { OpenCloseStateResult } from 'src/hooks/shared';

import {
  appContainerStyles,
  appContentWrapperStyles,
  avatarStyles,
  logoStyles,
  mainContainerStyles,
  menuContainerStyles,
  mobileAppContainerStyles,
  mobileDrawerHeaderStyles,
  mobileMenuContainerStyles,
} from './styles';

interface BaseLayoutProps {
  leftMenu: React.ReactNode;
  header: React.ReactNode;
  content: React.ReactNode;
  leftMenuDrawerState: OpenCloseStateResult<Record<string, never>>;
  currentUserFullName?: Nullable<string>;
  currentUserRoleLabel?: Nullable<string>;
  currentUserAvatarUrl?: Nullable<string>;
  defaultLogoUrl?: string;
  rootRoute?: string;
}

export const BaseLayout: React.FC<BaseLayoutProps> = ({
  leftMenu,
  header,
  content,
  leftMenuDrawerState,
  currentUserFullName,
  currentUserRoleLabel,
  currentUserAvatarUrl,
  defaultLogoUrl,
  rootRoute = '/',
}) => {
  const location = useLocation();
  const { isMobile } = useScreenInfo();

  const isExactHomePath = Boolean(matchPath({ path: rootRoute, end: false }, location.pathname));

  const userAvatar = React.useMemo(
    () => currentUserAvatarUrl || defaultLogoUrl,
    [currentUserAvatarUrl, defaultLogoUrl],
  );

  if (isMobile) {
    return (
      <Box sx={mainContainerStyles}>
        <Drawer
          open={leftMenuDrawerState.isOpened}
          anchor="right"
          transitionDuration={300}
          variant="temporary"
          onClose={() => leftMenuDrawerState.close()}
          sx={{
            [`.${drawerClasses.paper}`]: {
              width: '100%',
            },
          }}
          keepMounted
        >
          <Box sx={mobileDrawerHeaderStyles}>
            <Box sx={logoStyles}>
              <Avatar variant="circular" src={userAvatar} sx={avatarStyles} />
              <Box>
                <Typography variant="body1">{currentUserFullName}</Typography>
                {currentUserRoleLabel && (
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    {currentUserRoleLabel}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={() => leftMenuDrawerState.close()}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={mobileMenuContainerStyles(leftMenuDrawerState.isOpened)}>{leftMenu}</Box>
        </Drawer>

        <Box sx={mobileAppContainerStyles}>
          {header}
          <Box sx={appContentWrapperStyles(isExactHomePath)}>{content}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={mainContainerStyles}>
      <Drawer
        open={leftMenuDrawerState.isOpened}
        anchor="left"
        transitionDuration={300}
        variant="permanent"
      >
        <Box sx={menuContainerStyles(leftMenuDrawerState.isOpened)}>{leftMenu}</Box>
      </Drawer>

      <Box sx={appContainerStyles(leftMenuDrawerState.isOpened)}>
        {header}
        <Box sx={appContentWrapperStyles(isExactHomePath)}>{content}</Box>
      </Box>
    </Box>
  );
};
